import React, { useState, useEffect } from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Sun, Moon, Smartphone } from 'lucide-react';

import {
  LineaDownload,
  LineaSmartphone,
  LineaMonitor,
  LineaTarget,
  LineaClock,
  LineaRotate,
  LineaArrowRight,
  LineaLightning,
  LineaUsers,
  LineaSwords,
  LineaTrophy,
  LineaDoubleTriangleRight
} from "../components/LineaIcons";
import { getTranslation } from "../components/translations";

const LOGO_URL = "/v4logo.png";
const LOGO_LIGHT_URL = "/v4logo.png";

export default function Steps() {
  const t = (key) => getTranslation(key);

  usePageMeta({
    title: 'VSteps Setup Guide | Fitness Gaming Controller | Active Gaming',
    description: 'Set up VSteps — the lower-body fitness gaming controller that turns real walking and running into in-game movement. Step-by-step guide for Joy-Con pocket sensors.',
    ogImage: `${window.location.origin}/runner_icon2.png`,
    ogUrl: window.location.origin + '/Steps',
  });

  const [scrolled, setScrolled] = useState(false);
  const [triggerForce, setTriggerForce] = useState([4.0]);
  const [holdWindow, setHoldWindow] = useState([3.5]);
  const [sprintForce, setSprintForce] = useState([4.0]);
  const [burstEnabled, setBurstEnabled] = useState(false);
  const [turningSensitivity, setTurningSensitivity] = useState([150]);
  const [sprintEnabled, setSprintEnabled] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`min-h-screen overflow-x-hidden transition-colors duration-300 ${isDarkMode ? 'bg-[#0B0C10] text-white' : 'bg-gray-50 text-gray-900'}`} style={{ WebkitOverflowScrolling: 'touch' }}>
      {/* No ambient background glow - glow is on the logo only */}

      {/* Glassmorphism Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? isDarkMode 
              ? 'bg-black/90 backdrop-blur-xl border-b border-white/5'
              : 'bg-white/80 backdrop-blur-xl border-b border-gray-200'
            : isDarkMode 
              ? 'bg-black/70 backdrop-blur-lg'
              : 'bg-white/50 backdrop-blur-lg'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-3"
            whileHover={{ scale: 1.02 }}
          >
            <img 
              src={isDarkMode ? LOGO_URL : LOGO_LIGHT_URL} 
              alt="VSteps" 
              className="h-8 sm:h-10 w-auto"
            />
          </motion.div>

          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {[
              { label: t('faq'), id: 'faq' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`${isDarkMode ? 'text-[#C5C6C7] hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm font-medium tracking-wide transition-colors duration-300 relative group`}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF0000] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
            <button
              onClick={() => window.location.href = '/pricing'}
              className="text-[#FF0000] hover:text-white text-sm font-medium tracking-wide transition-colors duration-300 relative group"
            >
              {t('pricing')}
              <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-[#FF0000]" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsDarkMode(!isDarkMode)}
              variant="outline"
              size="icon"
              className={`${isDarkMode ? 'border-white/20 hover:bg-white/10' : 'border-gray-300 hover:bg-gray-100'}`}
            >
              {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className={`font-semibold px-4 sm:px-6 text-sm transition-all duration-300 ${isDarkMode ? 'border-white/20 text-white hover:bg-white/10' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              Home
            </Button>
            <Button 
              onClick={() => window.location.href = '/pricing'}
              className="bg-[#FF0000] hover:bg-[#FF0000]/90 text-white font-semibold px-4 sm:px-6 text-sm shadow-lg shadow-red-500/20 hover:shadow-red-500/40 transition-all duration-300"
            >
              {t('getStarted')}
            </Button>
          </div>
          </nav>
      </motion.header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 pt-20 pb-12 sm:pt-20 sm:pb-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative"
        >
          <div className="absolute inset-0 rounded-full bg-red-500/40 blur-[60px] scale-110 pointer-events-none" />
          <img 
            src="/vsteps_hero_logo.png" 
            alt="V4llued Steps" 
            className="relative w-32 h-32 md:w-44 md:h-44 object-contain"
          />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mt-6 sm:mt-8 md:mt-10 text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-center tracking-tight px-4"
        >
          <span className={`bg-clip-text text-transparent ${isDarkMode ? 'bg-gradient-to-r from-white via-white to-[#C5C6C7]' : 'bg-gradient-to-r from-gray-900 via-gray-900 to-gray-700'}`}>
            V4llued Steps{' '}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className={`mt-4 sm:mt-6 text-sm sm:text-base md:text-lg lg:text-xl text-center max-w-2xl leading-relaxed px-6 sm:px-4 ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}
        >
          {t('heroDescription').split('\n').map((line, i) => (
            <span key={i} className={isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}>{line}</span>
          ))}
        </motion.p>


        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mt-6 sm:mt-8 md:mt-10 flex flex-col gap-3 sm:gap-4 w-full items-center px-6 sm:px-4"
        >
          <a
            href="https://v4llued.weebly.com/v4llued-steps-desktop.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="button-pc-client"
            className="inline-flex items-center justify-center gap-3 bg-[#E82020] hover:bg-[#CC1A1A] text-white font-semibold rounded-lg hover:scale-105 transition-all duration-300 w-[300px] h-[60px] sm:h-[64px] text-sm sm:text-base shadow-lg hover:shadow-[0_0_25px_rgba(232,32,32,0.6),0_0_50px_rgba(232,32,32,0.4)] px-8"
          >
            <img src="/ms_logo_white.png" alt="" aria-hidden="true" width="150" height="150" className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0 object-contain" />
            <span>Download PC Client</span>
          </a>

          <a
            href="https://vsteps.io"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="button-vsteps-mobile"
            className="inline-flex items-center justify-center gap-3 bg-[#E82020] hover:bg-[#CC1A1A] text-white font-semibold rounded-lg hover:scale-105 transition-all duration-300 w-[300px] h-[60px] sm:h-[64px] text-sm sm:text-base shadow-lg hover:shadow-[0_0_25px_rgba(232,32,32,0.6),0_0_50px_rgba(232,32,32,0.4)] px-8"
          >
            <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
            <span>Open App on Android or iOS</span>
          </a>
        </motion.div>

        </section>


      {/* Setup Guide Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#FF0000] text-sm font-semibold tracking-[0.2em] uppercase">{t('quickSetup')}</span>
            <h2 className={`mt-4 text-3xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('setupGuide')}</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                icon: LineaMonitor,
                titleKey: "step1Title",
                descKey: "step1Desc"
              },
              {
                step: "02",
                icon: LineaSmartphone,
                titleKey: "step2Title",
                descKey: "step2Desc"
              },
              {
                step: "03",
                icon: LineaTarget,
                titleKey: "step3Title",
                descKey: "step3Desc"
              },
              {
                step: "04",
                icon: LineaLightning,
                titleKey: "step4Title",
                descKey: "step4Desc"
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative group"
              >
                <div className={`rounded-3xl p-6 border-2 transition-all duration-500 h-full ${isDarkMode ? 'bg-gradient-to-br from-[#181818] to-[#181818]/50 border-white/5 hover:border-[#FF0000]/30 hover:shadow-[0_0_30px_rgba(255,0,0,0.3)]' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/40 hover:shadow-lg'}`}>
                  <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#FF0000] flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-red-500/30">
                    {item.step}
                  </div>
                  
                  <div className="w-12 h-12 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-4 mt-2">
                    <item.icon className="w-6 h-6 text-[#FF0000]" />
                  </div>

                  <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t(item.titleKey)}</h3>
                  <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>{t(item.descKey)}</p>
                </div>
                
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <LineaArrowRight className="w-6 h-6 text-[#FF0000]/50" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customization Sliders Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#FF0000] text-sm font-semibold tracking-[0.2em] uppercase">{t('customization')}</span>
            <h2 className={`mt-4 text-3xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('customizationTitle')}</h2>
            <p className={`mt-4 max-w-2xl mx-auto text-lg ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
              {t('customizationDesc')}
            </p>
          </motion.div>

          <div className="space-y-8 max-w-2xl mx-auto">
            {/* Trigger Force */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`rounded-3xl p-8 border-2 transition-all duration-300 ${isDarkMode ? 'bg-gradient-to-br from-[#181818] to-[#181818]/70 border-white/5 hover:border-[#FF0000]/20 hover:shadow-[0_0_30px_rgba(255,0,0,0.3)]' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/40 hover:shadow-lg'}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-6 relative overflow-hidden">
                <svg className="w-7 h-7 text-[#FF0000]" viewBox="0 0 32 32" fill="currentColor">
                  <path d="M10 4 C7.5 4 6 6 6 8 C6 11 8 13 10 14 L10 18 C10 19.1 10.9 20 12 20 L14 20 C15.1 20 16 19.1 16 18 L16 14.5 C17.5 14 18.5 12.5 18.5 11 C18.5 8.5 16.5 6 14 5 C12.8 4.3 11.4 4 10 4 Z" />
                  <path d="M20 14 C17.5 14 16 16 16 18 C16 21 18 23 20 24 L20 28 C20 29.1 20.9 30 22 30 L24 30 C25.1 30 26 29.1 26 28 L26 24.5 C27.5 24 28.5 22.5 28.5 21 C28.5 18.5 26.5 16 24 15 C22.8 14.3 21.4 14 20 14 Z" opacity="0.6"/>
                </svg>
                <div className="absolute inset-0 bg-[#FF0000] opacity-10 blur-lg" />
              </div>
              
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Walk Mode</h3>
              <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                {t('triggerDesc')}
              </p>
              
              <div className="space-y-4">
                <Slider
                  value={triggerForce}
                  onValueChange={setTriggerForce}
                  min={1.0}
                  max={7.0}
                  step={0.1}
                  className="[&_[role=slider]]:bg-[#CC0000] [&_[role=slider]]:border-[#CC0000] [&>span]:bg-[#CC0000]/20 [&>span>span]:bg-[#CC0000]"
                />
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-600'}>1.0</span>
                  <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{triggerForce[0].toFixed(1)}</span>
                  <span className={isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-600'}>7.0</span>
                </div>
              </div>
            </motion.div>



            {/* Sprint Feature */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className={`rounded-3xl p-8 border-2 transition-all duration-300 ${isDarkMode ? 'bg-gradient-to-br from-[#181818] to-[#181818]/70 border-white/5 hover:border-[#FF0000]/20 hover:shadow-[0_0_30px_rgba(255,0,0,0.3)]' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/40 hover:shadow-lg'}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-6 relative overflow-hidden">
                <LineaDoubleTriangleRight className="w-7 h-7 text-[#FF0000]" />
                <div className="absolute inset-0 bg-[#FF0000] opacity-10 blur-lg" />
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sprint Mode</h3>
                <Switch
                  checked={sprintEnabled}
                  onCheckedChange={setSprintEnabled}
                  className="data-[state=checked]:bg-[#FF0000]"
                />
              </div>
              <p className={`text-sm mb-6 leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                {t('sprintDesc')}
              </p>

              <div className="space-y-4">
                <Slider
                  value={sprintForce}
                  onValueChange={setSprintForce}
                  min={1.0}
                  max={7.0}
                  step={0.1}
                  disabled={!sprintEnabled}
                  className="[&_[role=slider]]:bg-[#CC0000] [&_[role=slider]]:border-[#CC0000] [&>span]:bg-[#CC0000]/20 [&>span>span]:bg-[#CC0000] disabled:opacity-50"
                />
                <div className="flex justify-between text-sm">
                  <span className={isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-600'}>1.0</span>
                  <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{sprintForce[0].toFixed(1)}</span>
                  <span className={isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-600'}>7.0</span>
                </div>
              </div>

              <div className="flex items-center justify-between mt-6 pt-5 border-t border-white/10">
                <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Sprint as Toggle</span>
                <Switch
                  checked={burstEnabled}
                  onCheckedChange={setBurstEnabled}
                  className="data-[state=checked]:bg-white data-[state=unchecked]:bg-white/20 [&_span]:bg-gray-800 data-[state=unchecked]:grayscale"
                />
              </div>
            </motion.div>

            {/* W and Left Shift Remap */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className={`rounded-3xl p-8 border-2 transition-all duration-300 ${isDarkMode ? 'bg-gradient-to-br from-[#181818] to-[#181818]/70 border-white/5 hover:border-[#FF0000]/20 hover:shadow-[0_0_30px_rgba(255,0,0,0.3)]' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/40 hover:shadow-lg'}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-6 relative overflow-hidden">
                <svg className="w-7 h-7 text-[#FF0000]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 9h6M9 15h6" />
                </svg>
                <div className="absolute inset-0 bg-[#FF0000] opacity-10 blur-lg" />
              </div>
              
              <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('remapFeature')}</h3>
              <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                {t('remapDesc')}
              </p>
            </motion.div>
              </div>
              </div>
              </section>

              {/* Competitive Ecosystem Section */}
              <section className="py-12 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#FF0000] text-sm font-semibold tracking-[0.2em] uppercase">{t('competitive')}</span>
            <h2 className={`mt-4 text-3xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('competitiveTitle')}</h2>
            <p className={`mt-4 max-w-2xl mx-auto text-lg ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
              {t('competitiveDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Module 1: Shadow Network */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`group relative backdrop-blur-lg rounded-3xl p-8 border transition-all duration-500 ${isDarkMode ? 'bg-[#181818]/80 border-white/5 hover:border-[#FF0000]/50' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/50'}`}
              style={{
                boxShadow: '0 0 30px rgba(255, 0, 0, 0.1)'
              }}
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 rounded-3xl shadow-[0_0_40px_rgba(255,0,0,0.3)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF0000]/20 transition-all duration-300">
                  <LineaUsers className="w-8 h-8 text-[#FF0000]" />
                </div>
                
                <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('friends')}</h3>
                <p className={`leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                  {t('friendsDesc')}
                </p>
              </div>
            </motion.div>

            {/* Module 2: The Arena */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className={`group relative backdrop-blur-lg rounded-3xl p-8 border transition-all duration-500 hover:scale-105 ${isDarkMode ? 'bg-[#181818]/80 border-white/5 hover:border-[#FF0000]/50' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/50'}`}
              style={{
                boxShadow: '0 0 30px rgba(255, 0, 0, 0.1)'
              }}
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 rounded-3xl shadow-[0_0_40px_rgba(255,0,0,0.3)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF0000]/20 transition-all duration-300">
                  <LineaSwords className="w-8 h-8 text-[#FF0000]" />
                </div>
                
                <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('pvpArena')}</h3>
                <p className={`leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                  {t('pvpDesc')}
                </p>
              </div>
            </motion.div>

            {/* Module 3: Hall of Legends */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className={`group relative backdrop-blur-lg rounded-3xl p-8 border transition-all duration-500 ${isDarkMode ? 'bg-[#181818]/80 border-white/5 hover:border-[#FF0000]/50' : 'bg-gray-100 border-gray-300 hover:border-[#FF0000]/50'}`}
              style={{
                boxShadow: '0 0 30px rgba(255, 0, 0, 0.1)'
              }}
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 rounded-3xl shadow-[0_0_40px_rgba(255,0,0,0.3)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-[#FF0000]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF0000]/20 transition-all duration-300">
                  <LineaTrophy className="w-8 h-8 text-[#FF0000]" />
                </div>
                
                <h3 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('trophies')}</h3>
                <p className={`leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                  {t('trophiesDesc')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-transparent via-[#181818]/30 to-transparent">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#FF0000] text-sm font-semibold tracking-[0.2em] uppercase">{t('faqTitle')}</span>
            <h2 className={`mt-4 text-3xl md:text-5xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('faqSubtitle')}</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
          >
            <Accordion type="single" collapsible className="space-y-4">
              {[
                { questionKey: "faqQ2", answerKey: "faqA2" },
                { questionKey: "faqQ3", answerKey: "faqA3" },
                { questionKey: "faqQ4", answerKey: "faqA4" },
                { questionKey: "faqQ5", answerKey: "faqA5" }
              ].map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className={`rounded-2xl border px-6 data-[state=open]:border-[#FF0000]/30 transition-all duration-300 ${isDarkMode ? 'bg-[#181818]/60 border-white/5' : 'bg-gray-100 border-gray-300'}`}
                  >
                  <AccordionTrigger className="text-left font-semibold hover:text-[#FF0000] transition-colors py-5 [&[data-state=open]>svg]:text-[#FF0000]">
                    {t(item.questionKey)}
                  </AccordionTrigger>
                  <AccordionContent className={`pb-5 leading-relaxed ${isDarkMode ? 'text-[#C5C6C7]' : 'text-gray-700'}`}>
                    {item.answerKey === 'faqA5' ? (
                      <>
                        Yes, you can emulate Nintendo Joy-Con, PS4 (DS4), and PS5 (DS5) controllers like an Xbox controller using a free app called DS4Windows. The step-by-step guide will be on the <a href="https://linktr.ee/v4llued" target="_self" rel="noopener noreferrer" className="text-[#FF4444] hover:text-red-300 underline">Resources</a> page. If you want to use these controllers on a gaming console instead of a PC, you will need a device called a XIM Matrix.
                      </>
                    ) : t(item.answerKey)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>


      {/* Footer */}
      <footer className={`py-8 sm:py-12 px-4 sm:px-6 border-t ${isDarkMode ? 'border-white/5 bg-black' : 'border-gray-200 bg-white'}`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
          <div className="flex items-center gap-3 flex-wrap justify-center md:justify-start">
            <img 
              src={isDarkMode ? LOGO_URL : LOGO_LIGHT_URL} 
              alt="VSteps" 
              className="h-6 sm:h-8 w-auto"
            />
            <span className="text-[#C5C6C7] text-xs sm:text-sm text-center md:text-left">{t('allRightsReserved')}</span>
            </div>

            <div className="flex items-center gap-6 sm:gap-8 flex-wrap justify-center">
            <a href="/privacy" className="text-[#C5C6C7] hover:text-white text-xs sm:text-sm transition-colors">{t('privacy')}</a>
            <a href="/terms" className="text-[#C5C6C7] hover:text-white text-xs sm:text-sm transition-colors">{t('terms')}</a>
            <a href="https://linktr.ee/v4llued" target="_self" className="text-[#C5C6C7] hover:text-white text-xs sm:text-sm transition-colors">Resources</a>
            </div>
        </div>
      </footer>
    </div>
  );
}